import { ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  AppUpdate,
  AppUpdateAvailability,
  FlexibleUpdateInstallStatus,
} from '@capawesome/capacitor-app-update';
import { getEnv } from '../utils/get-env';

const DISMISS_KEY_PREFIX = 'app_update:dismissed';
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

const showUpdateModal = ref(false);
const availableVersion = ref('');
const isUpdating = ref(false);

let updateInfo: Awaited<ReturnType<typeof AppUpdate.getAppUpdateInfo>> | null = null;

const isDismissedRecently = async (version: string): Promise<boolean> => {
  const { value } = await Preferences.get({
    key: `${DISMISS_KEY_PREFIX}:${version}`,
  });

  if (!value) {
    return false;
  }

  const dismissedAt = new Date(value).getTime();

  return Date.now() - dismissedAt < COOLDOWN_MS;
};

const checkForUpdate = async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    updateInfo = await AppUpdate.getAppUpdateInfo();

    if (updateInfo.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
      return;
    }

    const version = updateInfo.availableVersionName || updateInfo.availableVersionCode || '';

    if (!version) {
      return;
    }

    if (await isDismissedRecently(version)) {
      return;
    }

    availableVersion.value = version;
    showUpdateModal.value = true;
  } catch {
    // Fail silently — update check is non-critical
  }
};

const performUpdate = async () => {
  if (!updateInfo) {
    return;
  }

  isUpdating.value = true;

  try {
    const platform = Capacitor.getPlatform();

    if (platform === 'android' && updateInfo.flexibleUpdateAllowed) {
      await AppUpdate.startFlexibleUpdate();

      AppUpdate.addListener('onFlexibleUpdateStateChange', async (state) => {
        if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
          await AppUpdate.removeAllListeners();
          await AppUpdate.completeFlexibleUpdate();
        }
      });
    } else {
      await AppUpdate.openAppStore({
        appId: getEnv('VITE_APP_IOS_APP_ID'),
      });
    }
  } catch {
    // Fallback to store if in-app update fails
    try {
      await AppUpdate.openAppStore({
        appId: getEnv('VITE_APP_IOS_APP_ID'),
      });
    } catch {
      // Nothing we can do
    }
  } finally {
    showUpdateModal.value = false;
    isUpdating.value = false;
  }
};

const dismissUpdate = async () => {
  if (availableVersion.value) {
    await Preferences.set({
      key: `${DISMISS_KEY_PREFIX}:${availableVersion.value}`,
      value: new Date().toISOString(),
    });
  }

  showUpdateModal.value = false;
};

export const useAppUpdate = () => {
  return {
    showUpdateModal,
    availableVersion,
    isUpdating,
    checkForUpdate,
    performUpdate,
    dismissUpdate,
  };
};
